/* eslint-disable prefer-arrow/prefer-arrow-functions */
/* eslint-disable no-bitwise */
/* eslint-disable max-len */
import { Injectable } from '@angular/core';
import { Filesystem, Directory, Encoding, GetUriOptions, ReaddirOptions } from '@capacitor/filesystem';
import { from, Observable } from 'rxjs';
import { AesService } from './aes.service';
import { Nugget } from './nugget.interface';
import { HttpClient } from '@angular/common/http';
import * as CryptoJS from 'crypto-js';
import { LogModel } from '../models/log';
import { LogService } from 'src/logs/log.service';
import { LogTypes } from 'src/logs/state/logs.actions';
import { NetworkDetectService } from 'src/app/services/network-detect.service';
import { first, takeUntil } from 'rxjs/operators';
import { SubscriptionDisposer } from 'src/app/shared/subscription-disposer';
import { Storage } from '@ionic/storage-angular';
import { TypeMessage } from 'src/app/layout/state/layout.actions';
import { PlayerFacade } from '../player.facade';

interface DownloadFileObject {
  base64: string;
  size: number;
  inCache: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class WebStorageService extends SubscriptionDisposer {

  private storageObj: Storage | null = null;

  constructor(private aesService: AesService,  private httpClient: HttpClient, private logService: LogService, private networkStatus: NetworkDetectService, private storage: Storage, private facade: PlayerFacade) {
    super();
    this.init();
  }

  async init() {
    console.log('storage init ');
    // If using, define drivers here: await this.storage.defineDriver(/*...*/);
    const storage = await this.storage.create();
    this.storageObj = storage;
  }

  // Create and expose methods that users of this service can
  // call, for example:
  public setFile(key: string, value: any) {
    return this.storageObj?.set(key, value).then((response: any)=>response).catch((error)=>{
        this.logService.sendMessage(`Web store set file error ${error}`, LogTypes.error);
        return null;
    });
  }

  public getFile(key: string): Promise<any> {
    return this.storageObj.get(key).then((value: any)=>value).catch((error)=>{
        this.logService.sendMessage(`Web store get file error ${error}`, LogTypes.error);
        return null;
    });
  }

  public deleteFile(key: string) {
    return this.storageObj.remove(key).then((value: any)=>value).catch((error)=>{
        this.logService.sendMessage(`Web store delete file error ${error}`, LogTypes.error);
        return null;
    });
  }

  async checkFileExists(filename: string): Promise<boolean> {
    const uri: GetUriOptions = {
      path: `/naistro/${filename}`,
      directory: Directory.Library
    };
    try {
      await Filesystem.stat(uri);
      return true;
    } catch (checkDirException) {
      // Log this only when debugging
      // if (checkDirException?.message !== 'File does not exist' && checkDirException?.message !== 'Entry does not exist.' && checkDirException !== 'Entry does not exist.') {
      // this.logService.sendMessage(`Some weird cache error checking for file ${checkDirException}`, LogTypes.error).pipe(
      //   first()
      // ).subscribe(()=>{},()=>{});
      // }
      return false;
    }
  }

  async getFileFromCache(file: string): Promise<DownloadFileObject>  {
      const size = (file.length - 814) / 1.37;
      const responsObject: DownloadFileObject = {
        base64 : file,
        size,
        inCache: true
      };
      return responsObject;
  }

  async newDownloadFile(url: string, md5: string): Promise<DownloadFileObject> {
    // check if file exists in cache
    const filename = url.slice(url.length-36,url.length);
    const fileExists = await this.getFile(filename);
    // file exists take from cache
    if (fileExists) {
      return this.getFileFromCache(fileExists).then((data)=>data);
    } else {
      // file not exists take from remote
      return await this.httpClient.get(url, { responseType: 'arraybuffer' }).toPromise().then(async (response)=>{
        // check md5 against downloaded mp3
        const wordArray = CryptoJS.lib.WordArray.create(response);
        const md5Generated = await CryptoJS.MD5(wordArray).toString();
        if (md5Generated.toUpperCase() !== md5.toUpperCase()) {
          this.logService.sendMessage(`[PLAYER]File corrupted on fetching: original hash: ${md5} generated hash: ${md5Generated}`, LogTypes.error);
          throw Error(`File corrupted on fetching: original hash: ${md5} generated hash: ${md5Generated}`);
        }

        const base64 = this.arrayBufferToBase64(response); // very important
        const size = (base64.length - 814) / 1.37;
        const responsObject: DownloadFileObject = {
          base64,
          size,
          inCache: false
        };
        return responsObject;
    });
  }
  }

  async writeDownloadedFile(url: string, md5: string) {
    const filename = url.slice(url.length-36,url.length);
    return this.newDownloadFile(url, md5).then(async (result)=>{
      if(result && !result.inCache) {
        // eslint-disable-next-line arrow-body-style
        return this.setFile(filename, result.base64).then(async (writeResult)=>{
            return { writeResult, size: result.size };
        });
      } else if(result) {
        return { size: result.size };
      } else if(!result || result===null) {
        return null;
      }
    });
  }

  getInfo() {
    return Promise.resolve(true);
  }

  writeFileNew(source: string, md5: string) {
    return new Observable(observer => {
      const timestampBeforeDownload = new Date().getTime();
      this.writeDownloadedFile(source, md5).then(writeFileResult=>{
        if(!writeFileResult) {
          this.logService.sendMessage(`[PLAYER][STORAGE] Error writing file, file not found in cache and updstream:: ${md5} ${source}`, LogTypes.error);
          observer.error(null);
          observer.complete();
        }
        const timestampAfterDownload = new Date().getTime();

        const response = {
          url: source,
          size: writeFileResult.size,
          downloadTime: (timestampAfterDownload - timestampBeforeDownload) / 1000,
        };

        observer.next(response);
        observer.complete();
      }).catch((error)=>{
          const file = {
            url: source,
            md5,
          };
            this.logService.sendMessage(`[PLAYER][STORAGE] Error reading and writing file, file not found in cache and could not be taken from upstream: ${file.url}`, LogTypes.error);
            // this.facade.showMessage('Play time not found in cache, attempting retry please wait  ', TypeMessage.info, 5000);

            // this.reloadApp();
          observer.error(error);
          observer.complete();
      });
    });
  }

  public reloadApp(waitTime = 3000) {
    setTimeout(()=>{
      console.log('5');
      window.location.reload();
    },waitTime);
  }

  async getFileNew(fileUrl: string, isEncrypted: boolean, nugget?: Nugget): Promise<string>  {
    const filename = fileUrl.slice(fileUrl.length-36,fileUrl.length);

    // eslint-disable-next-line arrow-body-style
    return this.getFile(filename).then(async (value)=>{
      if (!value) {
        if (this.networkStatus.getNetworkStatus() && !isEncrypted) {
          return fileUrl;
        } else {
          throw Error('File not found into the cache');
        }
      }
      if(value && isEncrypted && nugget) {
        const base64ToByte = this.base64ToBytes(value);

        const decrypt = await this.aesService.new2Decrypt(fileUrl, base64ToByte, value, nugget);

        const base64Decrypted = this.arrayBufferToBase64(decrypt); // in use

        return `data:audio/mp3;base64,${base64Decrypted}`;
      }
      return `data:audio/mp3;base64,${value}`;
    }).catch((error)=>{
      if (this.networkStatus.getNetworkStatus() && !isEncrypted) {
        return fileUrl;
      } else {
        this.logService.sendMessage(`[PLAYER][STORAGE] Error File not found in cache and is encrypted: ${fileUrl}`, LogTypes.error);
        throw Error('File not found in cache');
      }
    });
  }


  deleteOldestFile() {
    const dir: ReaddirOptions = {
      path: `files/naistro/fake`,
      directory: Directory.Library
    };
    return Filesystem.readdir(dir).then((value)=>{
      const filesOrderByDates = value.files.sort((a, b) => new Date(a.ctime).getTime() - new Date(b.ctime).getTime());
      const lastFile = filesOrderByDates[0];
      return Filesystem.deleteFile({path: `files/naistro${lastFile.name}`, directory: Directory.Library}).then(()=>lastFile.name);
    });
  }

  async deleteExpiredFiles(leeway) {
    const dir: ReaddirOptions = {
      path: `files/naistro/fake`,
      directory: Directory.Library
    };
    return Filesystem.readdir(dir).then((value)=>{
      const expiredFiles = value.files.filter(file => {
        const creationDate = new Date(file.ctime);
        const currentDate = new Date();
        const expireDate = new Date(creationDate.getTime() + leeway);
        return expireDate.getTime() <= currentDate.getTime();
      });
      return expiredFiles;
    });
  }

  checkAndDeleteExpiredFiles(leeway) {
    return from(this.deleteExpiredFiles(leeway));
  }

bufferDataToBase64(bytes) {
  let binary = '';
	const len = bytes.byteLength;
	for (let i = 0; i < len; i++) {
		binary += String.fromCharCode( bytes[ i ] );
	}
	return window.btoa( binary );
}

 arrayBufferToBase64( buffer: ArrayBuffer ) {
	let binary = '';
	const bytes = new Uint8Array( buffer );
	const len = bytes.byteLength;
	for (let i = 0; i < len; i++) {
		binary += String.fromCharCode( bytes[ i ] );
	}
	return window.btoa( binary );
}

base64ToArrayBuffer(base64) {
  const binaryString =  window.atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array( len );
  for (let i = 0; i < len; i++)        {
      bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

base64ToBytes(base64): Uint8Array {
  const binaryString =  window.atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array( len );
  for (let i = 0; i < len; i++)        {
      bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

async convertBlobToBase64(blob): Promise<any> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onloadend = () => {
      resolve(reader.result);
    };
    reader.readAsDataURL(blob);
  });
}

retrieveItem(folder: string): Observable<string | void> {
    return from(this.getFile(folder).then((value)=>value));
}

saveItem(item: string, folder: string) {
  this.setFile(folder, item).catch((err)=>{
    console.log('error saving item ', err);
  });
}



}
